import { useEffect, useState, useMemo } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ScrollElement from '../../../global/Elements/ScrollElement';
import { searchContext, userAppContext } from '../../../context/Context';
import GeneContent from '@/components/GeneContent/GeneContent';
import { scroller } from 'react-scroll';

import ManageGrid from './components/table/ManageGrid';
import ManageGraph from './components/table/ManageGraph';

import SummaryGDAGraphs from '@/components/Graphs/SummaryGDA/SummaryGDAGraphs';
import SummaryVDAGraphs from '@/components/Graphs/SummaryVDA/SummaryVDAGraphs';

import { useNavigate } from 'react-router-dom';
import MappingResults from '@/components/Mapping/MappingResults';
import { SearchContext, UserContext } from '../../../types';
import SharedButton from '../../../global/Button/Button';
import { getViewToggleText } from './helpers/graphHelpers';
import { getTabList } from './helpers/tableHelpers';
import DDAGraphs from '@/components/Graphs/DDA/DDAGraphs';
import { trackBrowseTabClick } from '../../../utils/analytics';
import ScoreModal from './components/modal/ScoreModal';
import SavedScoresDropdown from './components/dropdown/SavedScoresDropdown';
import { ChartPieIcon, TableCellsIcon } from '@heroicons/react/16/solid';
import { deleteFilter } from '../../../services/savedFiltersService';
import { mapItemsToSearchItems } from '../../../components/Table/types/index';
import SharedModal from '@/components/Modal/SharedModal';

interface SearchResultsProps {
  state: any;
  dispatch: any;
}

function SearchResults({ state, dispatch }: SearchResultsProps) {
  const navigate = useNavigate();
  const {
    view,
    source,
    tab,
    searchItems,
    setSearchItems,
    geneContent,
    setGeneContent,
    mappingContent,
    selectedInfoItem,
  } = searchContext() as unknown as SearchContext;
  const { restrictGraphDisplay, isAuthenticated, groups, restrictChemical } = userAppContext() as unknown as UserContext;
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedView, setSelectedView] = useState('tables');
  const [defaultFilterValue, _setDefaultFilterValue] = useState([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [refreshScoresDropdown, setRefreshScoresDropdown] = useState(0);
  const [sourceWeights, setSourceWeights] = useState<Array<{ name: string; score: number }>>(() => {
    // Load weights from sessionStorage for current tab
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');

  const handleViewChange = () => {
    setSelectedView(prevcurrentView => (prevcurrentView === 'tables' ? 'graphs' : 'tables'));
  };

  const handleApplyWeights = (weights: Array<{ name: string; score: number }>) => {
    setSourceWeights(weights);
    // Update weights for current tab when changed
    if (weights.length > 0) {
      sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(weights));
    } else {
      sessionStorage.removeItem(`sourceWeights_${tab}`);
    }
  };

  const handleApplySavedScore = (savedScore: any) => {
    if (savedScore.sourceWeights && savedScore.sourceWeights.length > 0) {
      handleApplyWeights(savedScore.sourceWeights);

      const urlParams = new URLSearchParams(window.location.search);
      if (savedScore.sourceWeights.length > 1) {
        urlParams.set('source', 'ALL');
      } else if (savedScore.sourceWeights.length === 1) {
        urlParams.set('source', savedScore.sourceWeights[0].name);
      }

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, '', newUrl);

      window.location.reload();
    }
  };

  const handleDeleteSavedScore = async (scoreId: string) => {
    try {
      if (!scoreId.startsWith('savedScore_')) {
        await deleteFilter(scoreId);
      }
    } catch (error) {
      console.error('Error deleting saved score:', error);
    }
  };

  const handleClearWeights = () => {
    // Clear weights
    setSourceWeights([]);
    sessionStorage.removeItem(`sourceWeights_${tab}`);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('source') === 'ALL') {
      // Could set to a default source, but for now just reload
    }

    // Reload to apply changes
    window.location.reload();
  };

  const tabList = useMemo(() => {
    return getTabList(view as string, tab as string);
  }, [view, tab]);

  const handleTabSelect = (index: number) => {
    // Simply proceed with tab change - weights are now tab-specific
    proceedWithTabChange(index);
  };

  const proceedWithTabChange = (index: number) => {
    setTabIndex(index);
    const newTab = tabList[index].key;
    const tabLabel = tabList[index].label;

    trackBrowseTabClick(tabLabel);

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tab', newTab);

    navigate({ pathname: window.location.pathname, search: `?${currentParams.toString()}` });
  };

  useEffect(() => {
    // Update nav component state based on app state
    const newIndex = tabList.findIndex(t => t.key === tab);
    setTabIndex(newIndex);

    // Load weights for the new tab
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        setSourceWeights(JSON.parse(stored));
      } catch (e) {
        setSourceWeights([]);
      }
    } else {
      setSourceWeights([]);
    }
  }, [tab, tabList]);

  // Table/Graph button
  // Button is only shown on Association views, and not CHEMICALS
  // const showManageGraph = view !== tab && ['GDA', 'VDA', 'DDA'].includes(tab);
  const showManageGraph = view !== 'ALL' && view !== 'CHEMICALS';

  // Grid view items
  const urlParams = new URLSearchParams(window.location.search);
  const isEvidenceParam = urlParams.get('isEvidence');

  let showSummary = true;
  let showEvidence = true;

  if (isEvidenceParam === 'false') {
    showEvidence = false;
  } else if (isEvidenceParam === 'true') {
    showSummary = false;
  } else {
    if (tab === 'DDA') showEvidence = false;
    if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) showEvidence = false;
  }

  // Graph view items
  const graphMap = {
    GDA: SummaryGDAGraphs,
    VDA: SummaryVDAGraphs,
    DDA: DDAGraphs,
  };

  useEffect(() => {
    // page should return to table view when last searchItem is removed
    if (searchItems?.length === 0) setSelectedView('tables');
  }, [searchItems]);

  // scroll to ref animation
  const scrollToRef = (ref: string) => {
    scroller.scrollTo(ref, {
      duration: 750,
      // delay: 50,
      // smooth: 'easeInOutQuad',
      smooth: true,
    });
  };

  useEffect(() => {
    if (geneContent!.isActive) {
      scrollToRef('Extra-Content');
    }
    if (mappingContent!.isActive) {
      scrollToRef('Mapping');
    }
  }, [geneContent!.isActive, mappingContent!.isActive]);

  // Auto-scroll to Evidence table if URL has only Evidence filters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let hasEvidenceFilters = false;
    let hasSummaryFilters = false;

    for (const [key] of params.entries()) {
      if (key.startsWith('filters-evidence-') || key.startsWith('filters[evidence][')) {
        hasEvidenceFilters = true;
      }
      if (key.startsWith('filters-summary-') || key.startsWith('filters[summary][')) {
        hasSummaryFilters = true;
      }
    }

    if (hasEvidenceFilters && !hasSummaryFilters && showEvidence) {
      // Small delay to ensure the component is mounted
      setTimeout(() => {
        scrollToRef('Evidence');
      }, 300);
    }
  }, [showEvidence]);

  return (
    <>
      {/* Score Modal */}
      <ScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        onApplyWeights={handleApplyWeights}
        onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
        refreshTrigger={refreshScoresDropdown}
      />

      {!isAuthenticated && view !== 'ALL' && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)' }}>
            As an unregistered user you are viewing only 10 results ordered by score for each query. To view
            more, register for an account. Learn more about our <a href="/plans">plans here</a>.
          </p>
        </div>
      )}
      {isTrial && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)', paddingTop: '10px' }}>
            Only the top 30 results are shown.{' '}
            <a href="/academic-plans" style={{ color: '#0d1661' }}>
              Upgrade your account{' '}
            </a>{' '}
            to see all results.{' '}
            <a href="/plans" style={{ color: '#0d1661' }}>
              View our plans
            </a>
            .
          </p>
        </div>
      )}
      <div
        className={`search-results-container ${selectedView === 'tables' ? 'flex' : ''}`}
        data-testid="search-results"
      >
        <div className="pt-5 pr-1 pl-1 md:p-5" style={{ flex: '1', height: '100%' }}>
          <Tabs
            className="react-tabs superposed-content"
            selectedIndex={tabIndex}
            onSelect={index => handleTabSelect(index)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <TabList className="react-tabs__tab-list">
                {tabList.map(t => (
                  <Tab key={t.key} className={'react-tabs__tab bg-white'}>
                    {/* <i className="fa fa-dot-circle-o" /> */}
                    {t.label}
                  </Tab>
                ))}
              </TabList>
              <div className="d-flex gap-2">
                {showManageGraph && !restrictGraphDisplay && (
                  <SharedButton size="md" onClick={() => handleViewChange()} className="h-9">
                    {selectedView === 'tables' ? (
                      <ChartPieIcon className="w-5 h-5 mr-2" />
                    ) : (
                      <TableCellsIcon className="w-5 h-5 mr-2" />
                    )}
                    View {getViewToggleText(selectedView)}
                  </SharedButton>
                )}
                {!isAcademic &&
                  selectedView === 'tables' &&
                  ['GDA', 'VDA'].includes(tab as string) &&
                  isAuthenticated && (
                    <SavedScoresDropdown
                      tab={tab as string}
                      onApplyScore={handleApplySavedScore}
                      onDeleteScore={handleDeleteSavedScore}
                      onConfigureNew={() => setScoreModalOpen(true)}
                      onClearWeights={handleClearWeights}
                      hasActiveWeights={sourceWeights.length > 0}
                      refreshTrigger={refreshScoresDropdown}
                    />
                  )}
              </div>
            </div>

            {/* Score Configuration Info Message */}
            {sourceWeights.length > 0 &&
              selectedView === 'tables' &&
              ['GDA', 'VDA'].includes(tab as string) &&
              !isAcademic &&
              isAuthenticated && (
                <div className="mb-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Custom Score Configuration Active</p>
                      <p className="text-xs text-blue-700 mt-1 mb-0">
                        You have a custom My Score configuration applied to this data.
                        {isAuthenticated ? (
                          <span>
                            {' '}
                            Use the "My Score" dropdown above to manage or clear your configuration.
                          </span>
                        ) : (
                          <span> Use the "My Score" button above to modify or clear your configuration.</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            <div>
              {tabList.map(t => (
                <TabPanel key={t.key} className="tab-pane">
                  <>
                    <ScrollElement name="Summary">
                      {selectedView === 'tables' && showSummary && (
                        <ManageGrid
                          source={source}
                          view={view}
                          tab={tab}
                          searchItems={mapItemsToSearchItems(searchItems)}
                          setSearchItems={setSearchItems}
                          isEvidence={false}
                          isMapping={false}
                          restrictChemical={restrictChemical}
                          isInfoSidebarOpen={isInfoSidebarOpen}
                          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                          defaultFilterValue={defaultFilterValue}
                          dispatch={dispatch}
                          sourceWeights={sourceWeights}
                          onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                        />
                      )}
                      {selectedView === 'graphs' && showManageGraph && (
                        <ManageGraph
                          source={source}
                          view={view}
                          tab={tab}
                          searchItems={searchItems}
                          setSearchItems={setSearchItems}
                          sectionTitle={t.label}
                          names={searchItems}
                          title={view}
                          Graph={graphMap[t.key]}
                          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                        />
                      )}
                    </ScrollElement>
                    <ScrollElement name="Evidence">
                      {selectedView === 'tables' && showEvidence && (
                        <ManageGrid
                          view={view}
                          source={source}
                          tab={tab}
                          searchItems={mapItemsToSearchItems(searchItems)}
                          setSearchItems={setSearchItems}
                          isEvidence={true}
                          isMapping={false}
                          restrictChemical={restrictChemical}
                          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                          isInfoSidebarOpen={isInfoSidebarOpen}
                          defaultFilterValue={defaultFilterValue}
                          dispatch={dispatch}
                          sourceWeights={sourceWeights}
                          onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                        />
                      )}
                    </ScrollElement>{' '}
                    <ScrollElement name="Mapping">
                      {selectedView === 'tables' && mappingContent?.isActive && (
                        <MappingResults mappingContent={mappingContent} dispatch={dispatch} />
                      )}
                    </ScrollElement>
                  </>
                </TabPanel>
              ))}
            </div>
          </Tabs>
          <div>
            {geneContent?.isActive && (
              <SharedModal
                open={geneContent.isActive}
                setOpen={(open: boolean) => {
                  if (!open) {
                    setGeneContent({
                      isActive: false,
                      geneInfo: { symbol: '', id: null, name: '' },
                    });
                  }
                }}
                title={geneContent.geneInfo?.symbol || 'Gene detail'}
                content={
                  <GeneContent
                    isActive={geneContent.isActive}
                    geneInfo={geneContent.geneInfo}
                    setGeneContent={setGeneContent}
                  />
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
export default SearchResults;
